"use client";

import { normalize, peakAmplitude } from "mediatools";
import { useTranslations } from "next-intl";
import { baseName } from "../_components/format";
import { OptionRadioGroup } from "../_components/OptionRadioGroup";
import type { MediaOperation, OperationOptionsProps } from "./types";

interface NormalizeOptions {
  targetDb: string;
}

/** Linear amplitude (0–1) to dBFS, with a floor for silence. */
function toDbfs(peak: number): string {
  if (peak <= 0) return "−∞";
  return (20 * Math.log10(peak)).toFixed(1);
}

function NormalizeOptionsForm({
  options,
  setOptions,
  ctx,
  disabled,
}: OperationOptionsProps<NormalizeOptions>) {
  const t = useTranslations("mediaTools.options.normalizeAudio");
  const targets = [
    { value: "-0.1", label: "-0.1 dBFS", hint: t("targets.maximum") },
    { value: "-1", label: "-1 dBFS", hint: t("targets.recommended") },
    { value: "-3", label: "-3 dBFS", hint: t("targets.headroom") },
  ];

  return (
    <div className="space-y-3">
      <OptionRadioGroup
        name="target"
        label={t("targetPeakLabel")}
        value={options.targetDb}
        onChange={(targetDb) => setOptions({ targetDb })}
        options={targets}
        disabled={disabled}
      />
      {ctx.decoded && (
        <p className="text-sm text-foreground/70">
          {t("currentPeak")}{" "}
          <span className="font-mono">{toDbfs(peakAmplitude(ctx.decoded))} dBFS</span>
        </p>
      )}
    </div>
  );
}

export const normalizeAudioOperation: MediaOperation<NormalizeOptions> = {
  id: "normalize-audio",
  group: "audio",
  inputKinds: ["audio"],
  outputKind: "audio",
  needsDecodedAudio: true,
  defaultOptions: { targetDb: "-1" },
  OptionsForm: NormalizeOptionsForm,
  run: async (ctx, options) => {
    if (!ctx.file || !ctx.decoded) throw new Error("No file loaded.");
    const pcm = normalize(ctx.decoded, { targetPeakDb: Number(options.targetDb) });
    return { pcm, baseName: `${baseName(ctx.file.name)}-normalized`, inputBytes: ctx.file.size };
  },
};
